<template>
    <Teleport to="body">
        <div
            v-if="show"
            class="fixed inset-0 z-[200] flex flex-col bg-white dark:bg-slate-800"
        >
            <!-- Header -->
            <div class="relative px-6 pt-6 pb-4" style="border-radius: 16px 16px 0 0">
                <button
                    type="button"
                    class="absolute right-4 top-5 p-2 rounded"
                    @click="cancel"
                >
                    <Icon icon="close" :width="20" :height="20" svg-class="text-slate-500" />
                </button>
                <h2 class="text-xl font-medium leading-7 text-slate-800 dark:text-slate-100 pr-12">
                    Customize dashboard
                </h2>
                <p class="mt-2 text-sm font-normal leading-5 text-slate-500 dark:text-slate-400">
                    Select the widgets you want to see on your dashboard
                </p>
            </div>

            <!-- Content (scrollable) -->
            <div class="flex-1 overflow-y-auto px-4 py-4">
                <!-- Your widgets -->
                <div class="flex flex-col gap-8">
                    <div>
                        <p class="text-sm font-medium leading-5 text-slate-500 dark:text-slate-400 mb-4">
                            Your widgets
                        </p>
                        <draggable
                            v-model="localVisibleWidgets"
                            item-key="widget_key"
                            :filter="'.no-drag'"
                            :animation="150"
                            ghost-class="mobile-ghost"
                            chosen-class="mobile-chosen"
                            drag-class="mobile-drag"
                            :group="{ name: 'mobile-widgets' }"
                            @change="onVisibleChange"
                            class="flex flex-col min-h-[48px]"
                            :class="localVisibleWidgets.length === 0 ? 'border border-dashed border-slate-300 dark:border-slate-700 rounded-lg items-center justify-center py-3' : 'gap-2'"
                        >
                            <template #item="{ element }">
                                <div class="flex items-center gap-2 py-3 px-2 self-stretch bg-white dark:bg-slate-700 rounded-lg shadow-sm cursor-grab" style="touch-action: none">
                                    <div class="p-1 text-slate-400">
                                        <Icon icon="drag-handle" :width="16" :height="16" />
                                    </div>
                                    <div class="shrink-0 text-slate-800 dark:text-slate-100" v-if="widgetIcons[element.widget_key]">
                                        <Icon :icon="widgetIcons[element.widget_key]" :width="20" :height="20" />
                                    </div>
                                    <span class="flex-1 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100">
                                        {{ widgetLabels[element.widget_key] }}
                                    </span>
                                    <button
                                        class="no-drag flex items-center justify-center w-5 h-5 text-slate-400 cursor-pointer"
                                        @click="hideWidget(element.widget_key)"
                                    >
                                        <Icon icon="eye-on" :width="20" :height="20" />
                                    </button>
                                </div>
                            </template>
                            <template #footer>
                                <p
                                    v-if="localVisibleWidgets.length === 0"
                                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400"
                                >
                                    Move widgets here to show them
                                </p>
                            </template>
                        </draggable>
                    </div>

                    <!-- Hidden widgets -->
                    <div>
                        <p class="text-sm font-medium leading-5 text-slate-500 dark:text-slate-400 mb-4 px-2">
                            Hidden widgets
                        </p>
                        <draggable
                            v-model="localHiddenWidgets"
                            item-key="widget_key"
                            :filter="'.no-drag'"
                            :animation="150"
                            ghost-class="mobile-ghost"
                            chosen-class="mobile-chosen"
                            drag-class="mobile-drag"
                            :group="{ name: 'mobile-widgets' }"
                            @change="onHiddenChange"
                            class="flex flex-col min-h-[48px]"
                            :class="localHiddenWidgets.length === 0 ? 'border border-dashed border-slate-300 dark:border-slate-700 rounded-lg items-center justify-center py-3' : 'gap-2'"
                        >
                            <template #item="{ element }">
                                <div class="flex items-center gap-2 py-3 px-2 self-stretch bg-white dark:bg-slate-700 rounded-lg shadow-sm cursor-grab" style="touch-action: none">
                                    <div class="p-1 text-slate-400">
                                        <Icon icon="drag-handle" :width="16" :height="16" />
                                    </div>
                                    <div class="shrink-0 text-slate-800 dark:text-slate-100" v-if="widgetIcons[element.widget_key]">
                                        <Icon :icon="widgetIcons[element.widget_key]" :width="20" :height="20" />
                                    </div>
                                    <span class="flex-1 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100">
                                        {{ widgetLabels[element.widget_key] }}
                                    </span>
                                    <button
                                        class="no-drag flex items-center justify-center w-5 h-5 text-slate-400 cursor-pointer"
                                        @click="showWidget(element.widget_key)"
                                    >
                                        <Icon icon="eye-off" :width="20" :height="20" />
                                    </button>
                                </div>
                            </template>
                            <template #footer>
                                <p
                                    v-if="localHiddenWidgets.length === 0"
                                    class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400"
                                >
                                    Move widgets here to hide them
                                </p>
                            </template>
                        </draggable>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="px-6 py-4 pb-12 bg-white dark:bg-slate-800 flex items-center gap-4">
                <button
                    type="button"
                    class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm"
                    @click="cancel"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium leading-5 text-white rounded shadow-sm"
                    style="background: #FD421D"
                    :disabled="dashboardStore.widgetPreferencesSaving"
                    @click="save"
                >
                    {{ dashboardStore.widgetPreferencesSaving ? 'Saving...' : 'Save changes' }}
                </button>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { ref, watch } from 'vue';
import draggable from 'vuedraggable';
import Icon from '@/components/ui/icon/icon.vue';
import { useDashboardStore } from '@/store/dashboard';

const props = defineProps({
    show: { type: Boolean, default: false },
    availableWidgetKeys: { type: Array, default: () => [] },
});

const emit = defineEmits(['close']);

const dashboardStore = useDashboardStore();

const localVisibleWidgets = ref([]);
const localHiddenWidgets = ref([]);

const widgetLabels = {
    my_next_card: 'My next card',
    credit_score: 'Credit score',
    courses: '10x Course',
    minimum_spend_deadlines: 'Minimum spend deadlines',
    travel_goals: 'Travel goals',
    card_benefits_monthly: 'My benefits',
    card_benefits_expiring: 'Benefits expiring soon',
    companion_pass: 'Companion pass',
    best_cards_by_category: 'My best cards by category',
};

const widgetIcons = {
    my_next_card: 'credit-card',
    credit_score: 'credit-score',
    courses: 'courses',
    minimum_spend_deadlines: 'calendar',
    travel_goals: 'globe',
    card_benefits_monthly: 'coin',
    card_benefits_expiring: 'coin',
    companion_pass: 'pass',
    best_cards_by_category: 'credit-card',
};

watch(
    () => props.show,
    (val) => {
        if (val) {
            dashboardStore.enterEditMode();
            syncFromStore();
        }
    }
);

watch(
    () => dashboardStore.widgetPreferences,
    () => {
        if (props.show) {
            syncFromStore();
        }
    },
    { deep: true }
);

function isAvailable(widgetKey) {
    return props.availableWidgetKeys.length === 0 || props.availableWidgetKeys.includes(widgetKey);
}

function syncFromStore() {
    localVisibleWidgets.value = [...dashboardStore.widgetPreferences]
        .filter((w) => w.visible && isAvailable(w.widget_key))
        .sort((a, b) => a.position - b.position);
    localHiddenWidgets.value = [...dashboardStore.widgetPreferences]
        .filter((w) => !w.visible && isAvailable(w.widget_key))
        .sort((a, b) => a.position - b.position);
}

function onVisibleChange(evt) {
    if (evt.added) {
        const widget = dashboardStore.widgetPreferences.find(
            (w) => w.widget_key === evt.added.element.widget_key
        );
        if (widget) widget.visible = true;
    }
    localVisibleWidgets.value.forEach((w, i) => {
        const pref = dashboardStore.widgetPreferences.find(
            (p) => p.widget_key === w.widget_key
        );
        if (pref) pref.position = i;
    });
}

function onHiddenChange(evt) {
    if (evt.added) {
        const widget = dashboardStore.widgetPreferences.find(
            (w) => w.widget_key === evt.added.element.widget_key
        );
        if (widget) widget.visible = false;
    }
}

function hideWidget(widgetKey) {
    dashboardStore.toggleWidgetVisibility(widgetKey);
    syncFromStore();
}

function showWidget(widgetKey) {
    dashboardStore.toggleWidgetVisibility(widgetKey);
    syncFromStore();
}

function cancel() {
    dashboardStore.cancelEditMode();
    emit('close');
}

async function save() {
    await dashboardStore.saveWidgetPreferences();
    if (!dashboardStore.isEditMode) {
        emit('close');
    }
}
</script>

<style scoped>
/* Ghost: the placeholder left behind — dimmed/greyed out */
.mobile-ghost {
    opacity: 0.4 !important;
}

/* Chosen: applied when item is picked up but before moving */
.mobile-chosen {
    background: #fff !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;
    border-radius: 8px !important;
    z-index: 10 !important;
}

/* Drag: the clone being dragged */
.mobile-drag {
    background: #fff !important;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15) !important;
    border-radius: 8px !important;
    opacity: 1 !important;
}
</style>
